import classNames from 'classnames';

import { IPsySetting } from '../../../store/psyTools.slice';
import { useAppSelector } from '../../../hooks/redux';
import { declOfNum } from '../../../utils/decOfNum';
import { Anger } from '../../icons/reactions/anger';
import { Fear } from '../../icons/reactions/fear';
import { Joy } from '../../icons/reactions/joy';
import { Love } from '../../icons/reactions/love';
import { Sadness } from '../../icons/reactions/sadness';

import { EmoList } from './emo-list';

export interface IEmoView {
  isPsiholog?: boolean;
  /** Карточный UI (слева смайлик+категория, справа теги) только для групповой терапии. */
  isGroup?: boolean;
  settings?: IPsySetting[];
  selectedEmo?: string[];
  onEmotionClick?: (item: string, selected: string[]) => void;
  reactionId: string[];
  setReactionId: React.Dispatch<React.SetStateAction<string[]>>;
}

export const EmoView = ({
  isPsiholog = false,
  isGroup = false,
  settings,
  selectedEmo,
  onEmotionClick,
  reactionId,
  setReactionId,
}: IEmoView) => {
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);

  const selectEmo = (a: string) => {
    if (reactionId.includes(a)) {
      setReactionId((prev) => prev.filter((el) => el !== a));

      return;
    }
    setReactionId((prev) => [...prev, a]);
  };

  const icoStyle = 'w-[50px] h-[50px] min-w-[50px] min-h-[50px]';
  const emotionNames = ['Любовь', 'Радость', 'Грусть', 'Страх', 'Гнев'];
  const emotionIcons = [Love, Joy, Sadness, Fear, Anger];

  const countEmo = (emo: string) => {
    const getLengthOfEmo = (a: string) => {
      const s = psyTools.find((el) => el.name === 'emotions_detection')?.psy_tool_settings;
      const arr = s?.find((el) => el.name === a)?.value;
      return arr?.length || 0;
    };
    return getLengthOfEmo(emo);
  };

  /** Карточный UI только в групповой терапии у клиента; в парной — старый вид (категории раскрываются по клику). */
  const isCardLayout = Boolean(isGroup && !isPsiholog && onEmotionClick && settings?.length);

  if (isCardLayout) {
    return (
      <div className="flex flex-col gap-4">
        {emotionNames.map((emo, index) => {
          const count = countEmo(emo);
          const items = settings?.find((el) => el.name === emo)?.value ?? [];
          const EmotionIcon = emotionIcons[index];

          return (
            <div
              key={emo}
              className={classNames(
                'flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 rounded-[10px] bg-white shadow-[0px_4px_12px_rgba(0,0,0,0.06)] border border-[#EAEBEA] min-w-0',
                { 'pb-[10rem]': index + 1 === emotionNames.length },
              )}
            >
              <div className="flex items-center gap-3 shrink-0">
                <div className="w-[80px] h-[80px] min-w-[80px] min-h-[80px] rounded-[10px] bg-grey5 flex items-center justify-center">
                  <EmotionIcon className={icoStyle} stroke="#63656A" />
                </div>
                <div className="flex flex-col">
                  <p className="font-manrope font-bold text-[#2C3038] text-lg">{emo}</p>
                  <p className="font-manrope text-[#585858] text-sm">
                    {count} {declOfNum(count, ['эмоция', 'эмоции', 'эмоций'])}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center min-w-0 flex-1">
                {items.map((item) => {
                  const selected = selectedEmo?.includes(item);
                  return (
                    <button
                      key={item}
                      type="button"
                      className={classNames(
                        'px-3 py-1.5 rounded-[5px] text-sm font-medium transition-colors border hover:border-[#D3B364]',
                        selected
                          ? 'bg-[#D3B364] text-[#2C3038] border-[#D3B364]'
                          : 'bg-[#F5F5F5] text-[#2C3038] border-[#E5E5E5] hover:bg-[#EBEBEB]',
                      )}
                      onClick={() => onEmotionClick?.(item, selectedEmo ?? [])}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={'flex flex-col xl:gap-8 gap-4 hover:cursor-pointer'}>
      {emotionNames.map((emo, index) => {
        const EmotionIcon = emotionIcons[index];
        return (
          <div
            key={emo}
            className={classNames('flex flex-col gap-4', {
              'pb-[10rem]': reactionId.length > 0 && index + 1 === emotionNames.length,
            })}
          >
            <div className={'flex items-center gap-[15px] hover:cursor-pointer'} onClick={() => selectEmo(emo)}>
              <EmotionIcon className={icoStyle} stroke="#fff" />
              <span className="text-white text-sm lx:text-4xl">
                {emo}
                {isPsiholog && (
                  <>
                    <br />
                    {`(${countEmo(emo)} ${declOfNum(countEmo(emo), ['эмоция', 'эмоции', 'эмоций'])})`}
                  </>
                )}
              </span>
            </div>
            {reactionId.includes(emo) && (
              <EmoList
                items={settings?.find((el) => el.name === emo)?.value ?? []}
                selectedEmo={selectedEmo}
                onEmotionClick={onEmotionClick}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
